import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import AddSeasonMutation from "./mutations/AddSeason";
import React, { useEffect, useMemo, useState } from "react";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import {Divider} from "@mui/material";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function AddSeasonModal({
                                           show,
                                           onClose,
                                           division,
                                           seasons,
                                           setSeasons,
                                           waivers,
                                       }) {
    const colors = useThemeColors();
    const { t } = useTranslation();
    const [addSeason] = AddSeasonMutation();
    const [selectedWaiverIds, setSelectedWaiverIds] = useState([]);

    const [useOvertime, setUseOvertime] = useState(false);
    const [registrationFeeEnabled, setRegistrationFeeEnabled] = useState(false);
    const [registrationFeeType, setRegistrationFeeType] = useState(
        "mandatory_on_registration"
    );
    const [lateRegistrationFee, setLateRegistrationFee] = useState("");
    const [registrationFeeAmount, setRegistrationFeeAmount] = useState("");
    const [registrationFeeDueDate, setRegistrationFeeDueDate] = useState("");
    const [registrationFeeInstallments, setRegistrationFeeInstallments] = useState([
        { amount: "", dueDate: "" },
    ]);
    const selectedWaiverTitles = useMemo(() => {
        const selectedSet = new Set((selectedWaiverIds || []).map((id) => String(id)));
        return (waivers || [])
            .filter((waiver) => selectedSet.has(String(waiver.id)))
            .map((waiver) => waiver.title);
    }, [selectedWaiverIds, waivers]);

    const modalStyles = useMemo(() => {
        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
                background: "#fff",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
          135deg,
          ${alphaFromHex(colors.primary, 0.5)},
          ${alphaFromHex(colors.primary, 0)}
        )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: {
                padding: 14,
                background: "#fff",
            },
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: "rgba(20,21,26,0.78)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            labelFlagged: {
                fontSize: 12.5,
                fontWeight: 800,
                color: colors.danger,
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 800,
                outline: "none",
            },
            select: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "0px 12px",
                height: 44,
                fontWeight: 800,
                outline: "none",
                backgroundColor: "#fff",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnDangerLink: {
                border: "none",
                background: "transparent",
                color: GlobalStyles.colors.lose || "#dc3545",
                fontWeight: 900,
                padding: "8px 10px",
            },
            btnOutline: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
                fontWeight: 900,
                borderRadius: 12,
                padding: "8px 10px",
            },
        };
    }, [colors.textOnPrimary, colors.primary, colors.danger]);

    const forceUnlockPage = () => {
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("overflow");
    };

    const safeClose = () => {
        forceUnlockPage();
        onClose?.();
        setTimeout(forceUnlockPage, 0);
        setTimeout(forceUnlockPage, 150);
    };

    useEffect(() => {
        if (show) {
            document.body.classList.add("modal-open");
            document.body.style.overflow = "hidden";
        } else {
            forceUnlockPage();
        }
        return () => forceUnlockPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    if (!show) return null;

    const isSoccerPoints = !["baseball", "softball"].includes(division?.sport?.key);
    const getIntValue = (form, name, fallback = 0) => {
        const value = form.elements.namedItem(name)?.value;
        const parsedValue = parseInt(value, 10);
        return Number.isNaN(parsedValue) ? fallback : parsedValue;
    };
    const getDateValue = (form, name) => {
        const value = form.elements.namedItem(name)?.value;
        return value || null;
    };

    async function handleSubmit(e) {
        e.preventDefault();
        const form = e.target;

        let registrationFeeInput = null;
        if (registrationFeeEnabled) {
            if (registrationFeeType === "installments") {
                registrationFeeInput = {
                    type: registrationFeeType,
                    installments: registrationFeeInstallments.map((inst, idx) => ({
                        amount: parseFloat(inst.amount),
                        dueDate: inst.dueDate,
                        installmentNumber: idx + 1,
                    })),
                };
            } else {
                registrationFeeInput = {
                    type: registrationFeeType,
                    amount: parseFloat(registrationFeeAmount),
                    dueDate: registrationFeeDueDate,
                };
            }
        }

        const lateRegistrationFeeInput =
            registrationFeeEnabled && lateRegistrationFee !== ""
                ? parseFloat(lateRegistrationFee)
                : null;

        try {
            const res = await addSeason({
                variables: {
                    input: {
                        name: form.elements.namedItem("name").value,
                        divisionSlug: division.slug,
                        registrationStartDate: getDateValue(form, "registration_start_date"),
                        registrationEndDate: getDateValue(form, "registration_end_date"),
                        lateRegistrationUntil: getDateValue(form, "late_registration_until"),
                        startDate: getDateValue(form, "start_date"),
                        endDate: getDateValue(form, "end_date"),
                        pointSystem: isSoccerPoints
                            ? {
                                winPoints: getIntValue(form, "win_points", 1),
                                drawPoints: getIntValue(form, "draw_points", 0),
                                losePoints: getIntValue(form, "lose_points", 0),
                                useOvertime: form.elements.namedItem("use_overtime").checked,
                                otWinPoints: form.elements.namedItem("use_overtime").checked
                                    ? getIntValue(form, "ot_win_points", 1)
                                    : getIntValue(form, "win_points", 1),
                                otLossPoints: form.elements.namedItem("use_overtime").checked
                                    ? getIntValue(form, "ot_loss_points", 0)
                                    : getIntValue(form, "lose_points", 0),
                                forfeitWinPoints: getIntValue(form, "forfeit_win_points", 3),
                                forfeitLossPoints: getIntValue(form, "forfeit_loss_points", 0),
                                forfeitWinScore: getIntValue(form, "forfeit_win_score", 3),
                                forfeitLossScore: getIntValue(form, "forfeit_loss_score", 0),
                                doubleForfeitPoints: getIntValue(form, "double_forfeit_points", 0),
                            }
                            : null,
                        waiverIds: selectedWaiverIds,
                        registrationFee: registrationFeeInput,
                        lateRegistrationFee: lateRegistrationFeeInput,
                    },
                },
            });

            if (res.data?.addSeason?.season) {
                setSeasons([...seasons, res.data.addSeason.season]);

                toast.success(t("addSeasonModal.toast.success"), {
                    autoClose: 3000,
                    toastId: "season_added",
                    position: "top-center",
                    closeOnClick: true,
                });

                safeClose();
                setSelectedWaiverIds([]);
                setTimeout(() => window.location.reload(), 3000);
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                autoClose: 5000,
                toastId: "season_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    }

    return (
        <>
            <div
                id="add-season-modal"
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                aria-labelledby="addSeasonModal"
            >
                <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <Form method="post" onSubmit={handleSubmit}>
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>{t("addSeasonModal.badge")}</span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("addSeasonModal.title")}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={safeClose}
                                    aria-label={t("addSeasonModal.buttons.close")}
                                    style={modalStyles.closeBtn}
                                    title={t("addSeasonModal.buttons.close")}
                                >
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            fontSize: 20,
                                            lineHeight: 1,
                                            color: colors.textOnPrimary,
                                        }}
                                    >
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>{t("addSeasonModal.sections.season")}</div>

                                    <div className="row">
                                        <div className="col-sm-4">
                                            <div className="form-group" style={{ marginBottom: 12 }}>
                                                <label style={modalStyles.label}>{t("addSeasonModal.fields.name")}</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    required
                                                    name="name"
                                                    placeholder={t("addSeasonModal.placeholders.seasonName")}
                                                    style={modalStyles.input}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-4">
                                            <div className="form-group" style={{ marginBottom: 12 }}>
                                                <label style={modalStyles.label}>{t("addSeasonModal.fields.startDate")}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    required
                                                    name="start_date"
                                                    style={modalStyles.input}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-4">
                                            <div className="form-group" style={{ marginBottom: 12 }}>
                                                <label style={modalStyles.label}>{t("addSeasonModal.fields.endDate")}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="end_date"
                                                    style={modalStyles.input}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-sm-4">
                                            <div className="form-group" style={{ marginBottom: 12 }}>
                                                <label style={modalStyles.label}>
                                                    {t("seasonList.headers.registrationStart")}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="registration_start_date"
                                                    style={modalStyles.input}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-4">
                                            <div className="form-group" style={{ marginBottom: 12 }}>
                                                <label style={modalStyles.label}>
                                                    {t("seasonList.headers.registrationEnd")}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="registration_end_date"
                                                    style={modalStyles.input}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-4">
                                            <div className="form-group" style={{ marginBottom: 12 }}>
                                                <label style={modalStyles.label}>
                                                    {t("addSeasonModal.fields.lateRegistrationUntil")}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="late_registration_until"
                                                    style={modalStyles.input}
                                                />
                                            </div>
                                        </div>
                                    </div>


                                </div>

                                {isSoccerPoints && (
                                    <div style={modalStyles.card}>
                                        <div style={modalStyles.sectionTitle}>{t("addSeasonModal.sections.pointSystem")}</div>

                                            <div className="row">
                                                <div className="col-sm-4">
                                                <div className="form-group" style={{ marginBottom: 12 }}>
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.winPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        required
                                                        name="win_points"
                                                        placeholder={t("addSeasonModal.placeholders.winPoints")}
                                                        defaultValue="1"
                                                        style={modalStyles.input}
                                                    />
                                                </div>
                                            </div>

                                                <div className="col-sm-4">
                                                <div className="form-group" style={{ marginBottom: 12 }}>
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.drawPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        required
                                                        name="draw_points"
                                                        placeholder={t("addSeasonModal.placeholders.drawPoints")}
                                                        defaultValue="0"
                                                        style={modalStyles.input}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-4">
                                                <div className="form-group" style={{ marginBottom: 12 }}>
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.losePoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        required
                                                        name="lose_points"
                                                        placeholder={t("addSeasonModal.placeholders.losePoints")}
                                                        defaultValue="0"
                                                        style={modalStyles.input}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div style={modalStyles.hint}>{t("addSeasonModal.hints.forfeit")}</div>
                                        <div className="row" style={{ marginTop: 12 }}>
                                            <div className="col-sm-4">
                                                <div className="form-group" style={{ marginBottom: 12 }}>
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.forfeitWinPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        required
                                                        name="forfeit_win_points"
                                                        placeholder={t("addSeasonModal.placeholders.forfeitWinPoints")}
                                                        defaultValue="3"
                                                        style={modalStyles.input}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-4">
                                                <div className="form-group" style={{ marginBottom: 12 }}>
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.forfeitLossPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        required
                                                        name="forfeit_loss_points"
                                                        placeholder={t("addSeasonModal.placeholders.forfeitLossPoints")}
                                                        defaultValue="0"
                                                        style={modalStyles.input}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-4">
                                                <div className="form-group" style={{ marginBottom: 12 }}>
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.doubleForfeitPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        required
                                                        name="double_forfeit_points"
                                                        placeholder={t("addSeasonModal.placeholders.doubleForfeitPoints")}
                                                        defaultValue="0"
                                                        style={modalStyles.input}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-sm-4">
                                                <div className="form-group" style={{ marginBottom: 12 }}>
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.forfeitWinScore")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        required
                                                        name="forfeit_win_score"
                                                        placeholder={t("addSeasonModal.placeholders.forfeitWinScore")}
                                                        defaultValue="3"
                                                        style={modalStyles.input}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-4">
                                                <div className="form-group" style={{ marginBottom: 12 }}>
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.forfeitLossScore")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        required
                                                        name="forfeit_loss_score"
                                                        placeholder={t("addSeasonModal.placeholders.forfeitLossScore")}
                                                        defaultValue="0"
                                                        style={modalStyles.input}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row" style={{ marginTop: 6 }}>
                                            <div className="col-sm-6">
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        name="use_overtime"
                                                        id="useOvertime"
                                                        checked={useOvertime}
                                                        onChange={(e) => setUseOvertime(e.target.checked)}
                                                    />
                                                    <label className="form-check-label" htmlFor="useOvertime">
                                                        <strong>{t("addSeasonModal.toggles.useOvertime")}</strong>
                                                    </label>
                                                </div>
                                                <div style={modalStyles.hint}>{t("addSeasonModal.hints.overtime")}</div>
                                            </div>
                                        </div>

                                        {useOvertime && (
                                            <div className="row" style={{ marginTop: 12 }}>
                                                <div className="col-sm-4">
                                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                                        <label style={modalStyles.label}>
                                                            {t("addSeasonModal.fields.overtimeWinPoints")}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            required
                                                            name="ot_win_points"
                                                            placeholder={t("addSeasonModal.placeholders.overtimeWinPoints")}
                                                            defaultValue="1"
                                                            style={modalStyles.input}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-sm-4">
                                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                                        <label style={modalStyles.label}>
                                                            {t("addSeasonModal.fields.overtimeLosePoints")}
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            required
                                                            name="ot_loss_points"
                                                            placeholder={t("addSeasonModal.placeholders.overtimeLosePoints")}
                                                            defaultValue="0"
                                                            style={modalStyles.input}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>{t("addSeasonModal.sections.policies")}</div>

                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label style={modalStyles.label}>{t("addSeasonModal.fields.waiverOptional")}</label>
                                        <div className="kbola-waiver-picker-wrap">
                                            <select
                                                className="form-control kbola-waiver-picker"
                                                name="waiverIds"
                                                style={modalStyles.select}
                                                multiple
                                                value={selectedWaiverIds}
                                                onChange={(e) =>
                                                    setSelectedWaiverIds(
                                                        Array.from(e.target.selectedOptions).map((opt) => opt.value)
                                                    )
                                                }
                                                size={Math.min(6, Math.max(3, waivers?.length || 0))}
                                            >
                                                {waivers?.map((waiver) => (
                                                    <option key={waiver.id} value={waiver.id}>
                                                        {waiver.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {selectedWaiverTitles.length > 0 && (
                                            <div className="kbola-waiver-chip-row">
                                                {selectedWaiverTitles.map((title, index) => (
                                                    <span className="kbola-waiver-chip" key={`${title}-${index}`}>
                                                        {title}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div style={modalStyles.hint}>{t("addSeasonModal.hints.waiver")}</div>
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>{t("addSeasonModal.sections.registrationFee")}</div>

                                    <div className="form-check" style={{ marginBottom: 12 }}>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="registrationFeeEnabled"
                                            checked={registrationFeeEnabled}
                                            onChange={() => setRegistrationFeeEnabled((v) => !v)}
                                        />
                                        <label className="form-check-label" htmlFor="registrationFeeEnabled">
                                            <strong>{t("addSeasonModal.toggles.enableRegistrationFee")}</strong>
                                        </label>
                                    </div>

                                    {registrationFeeEnabled && (
                                        <>
                                            <div className="row" style={{ marginBottom: 12 }}>
                                                <div className="col-sm-6 col-md-4">
                                                    <label style={modalStyles.label}>{t("addSeasonModal.fields.feeType")}</label>
                                                    <select
                                                        className="form-control"
                                                        value={registrationFeeType}
                                                        onChange={(e) => setRegistrationFeeType(e.target.value)}
                                                        style={modalStyles.select}
                                                    >
                                                        <option value="mandatory_on_registration">
                                                            {t("addSeasonModal.options.mandatoryOnRegistration")}
                                                        </option>
                                                        <option value="pay_later">{t("addSeasonModal.options.payLater")}</option>
                                                        <option value="installments">{t("addSeasonModal.options.installments")}</option>
                                                    </select>
                                                </div>


                                            </div>

                                            {registrationFeeType !== "installments" && (
                                                <div className="row" style={{ marginBottom: 6 }}>
                                                    <div className="col-sm-4">
                                                        <label style={modalStyles.label}>{t("addSeasonModal.fields.amount")}</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            required={registrationFeeEnabled}
                                                            min={0}
                                                            step="0.01"
                                                            value={registrationFeeAmount}
                                                            onChange={(e) => setRegistrationFeeAmount(e.target.value)}
                                                            placeholder={t("addSeasonModal.placeholders.amountExample")}
                                                            style={modalStyles.input}
                                                        />
                                                    </div>

                                                    <div className="col-sm-4">
                                                        <label style={modalStyles.label}>{t("addSeasonModal.fields.dueDate")}</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            required={registrationFeeEnabled}
                                                            value={registrationFeeDueDate}
                                                            onChange={(e) => setRegistrationFeeDueDate(e.target.value)}
                                                            style={modalStyles.input}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {registrationFeeType === "installments" && (
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={modalStyles.hint}>{t("addSeasonModal.hints.installments")}</div>

                                                    <div style={{ marginTop: 10 }}>
                                                        {registrationFeeInstallments.map((installment, idx) => (
                                                            <div className="row align-items-end" style={{ marginBottom: 10 }} key={idx}>
                                                                <div className="col-5 col-md-4">
                                                                    <label style={modalStyles.label}>
                                                                        {t("addSeasonModal.installment.amountNumber", {
                                                                            number: idx + 1,
                                                                        })}
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control"
                                                                        required={registrationFeeEnabled}
                                                                        min={0}
                                                                        step="0.01"
                                                                        placeholder={t("addSeasonModal.placeholders.installmentAmountExample")}
                                                                        value={installment.amount}
                                                                        onChange={(e) => {
                                                                            const updated = [...registrationFeeInstallments];
                                                                            updated[idx].amount = e.target.value;
                                                                            setRegistrationFeeInstallments(updated);
                                                                        }}
                                                                        style={modalStyles.input}
                                                                    />
                                                                </div>

                                                                <div className="col-5 col-md-4">
                                                                    <label style={modalStyles.label}>
                                                                        {t("addSeasonModal.installment.dueDateNumber", {
                                                                            number: idx + 1,
                                                                        })}
                                                                    </label>
                                                                    <input
                                                                        type="date"
                                                                        className="form-control"
                                                                        required={registrationFeeEnabled}
                                                                        value={installment.dueDate}
                                                                        onChange={(e) => {
                                                                            const updated = [...registrationFeeInstallments];
                                                                            updated[idx].dueDate = e.target.value;
                                                                            setRegistrationFeeInstallments(updated);
                                                                        }}
                                                                        style={modalStyles.input}
                                                                    />
                                                                </div>

                                                                <div className="col-2 col-md-2" style={{ display: "flex" }}>
                                                                    {registrationFeeInstallments.length > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setRegistrationFeeInstallments(
                                                                                    registrationFeeInstallments.filter((_, i) => i !== idx)
                                                                                );
                                                                            }}
                                                                            style={modalStyles.btnDangerLink}
                                                                            title={t("addSeasonModal.installment.removeTitle")}
                                                                        >
                                                                            {t("addSeasonModal.buttons.remove")}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}

                                                        <button
                                                            type="button"
                                                            style={modalStyles.btnOutline}
                                                            onClick={() =>
                                                                setRegistrationFeeInstallments([
                                                                    ...registrationFeeInstallments,
                                                                    { amount: "", dueDate: "" },
                                                                ])
                                                            }
                                                        >
                                                            {t("addSeasonModal.buttons.addInstallment")}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            <Divider style={{ margin: "20px 0" }} />

                                            <div className="col-sm-6 col-md-4">
                                                <label style={modalStyles.labelFlagged}>{t("addSeasonModal.fields.lateRegistrationFee")}</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min={0}
                                                    step="0.01"
                                                    value={lateRegistrationFee}
                                                    onChange={(e) => setLateRegistrationFee(e.target.value)}
                                                    placeholder={t("addSeasonModal.placeholders.amountExample")}
                                                    style={modalStyles.input}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={safeClose}
                                    style={{ ...modalStyles.btn, ...modalStyles.btnGhost }}
                                >
                                    <i className="fas fa-times" />
                                    {t("addSeasonModal.buttons.close")}
                                </button>

                                <button
                                    type="submit"
                                    className="btn"
                                    style={{ ...modalStyles.btn, ...modalStyles.btnPrimary }}
                                >
                                    <i className="fas fa-check" />
                                    {t("addSeasonModal.buttons.save")}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
        #add-season-modal.modal {
          background: rgba(0, 0, 0, 0.45);
          backdrop-filter: blur(2px);
        }

        #add-season-modal .modal-dialog {
          max-height: 92vh;
          display: flex;
          flex-direction: column;
          margin-top: 1.25rem;
          margin-bottom: 1.25rem;
        }

        #add-season-modal .modal-content {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        #add-season-modal .modal-body {
          overflow-y: auto;
          max-height: 70vh;
        }

        #add-season-modal .form-control:focus {
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9);
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }

        #add-season-modal .kbola-waiver-picker-wrap {
          border-radius: 12px;
          border: 1px solid rgba(0, 0, 0, 0.11);
          background: linear-gradient(180deg, rgba(255,255,255,0.94), rgba(247,248,250,0.92));
          padding: 8px;
        }

        #add-season-modal .kbola-waiver-picker {
          min-height: 132px;
          height: auto !important;
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }

        #add-season-modal .kbola-waiver-picker option {
          border-radius: 9px;
          margin: 2px 0;
          padding: 9px 10px;
          font-weight: 700;
        }

        #add-season-modal .kbola-waiver-picker option:checked {
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.72);
          color: #14151a;
        }

        #add-season-modal .kbola-waiver-picker-wrap:focus-within {
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.95);
          box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
        }

        #add-season-modal .kbola-waiver-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 8px;
        }

        #add-season-modal .kbola-waiver-chip {
          font-size: 11px;
          font-weight: 800;
          border-radius: 999px;
          padding: 5px 9px;
          color: rgba(20, 21, 26, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.1);
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.42);
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        #add-season-modal .kbola-waiver-picker::-webkit-scrollbar {
          width: 10px;
        }

        #add-season-modal .kbola-waiver-picker::-webkit-scrollbar-thumb {
          border-radius: 999px;
          border: 2px solid rgba(255,255,255,0.95);
          background: rgba(20, 21, 26, 0.24);
        }

        @media (max-width: 768px) {
          #add-season-modal .modal-dialog {
            max-width: 95%;
            margin: 0.9rem auto;
          }
          #add-season-modal .modal-body {
            max-height: 74vh;
          }
        }
      `}</style>
        </>
    );
}
